import axios from 'axios';
import { merge } from '../utility/common';
import {
  DEFAULT_HEADERS_BY_SERVICE,
  DEFAULT_TIMEOUTS,
  HTTP_METHODS
} from './api.constants';
import {
  axiosRejectedResponseInterceptor,
  axiosSuccessResponseInterceptor
} from './api.error.helpers';
import { setHeaders } from './api.headers';
import {
  ApiInstance,
  ApiInstanceConfiguration,
  ApiOptions,
  ApiParams
} from './api.interfaces';
import { updateAuthorizationHeader } from './axios.interceptors';
import { ApiRequestMap, ApiFaultMap, ApiResponseMap } from 'invApi';

/** Default Axios instance for services API requests. */
let servicesRequestInstance: ApiInstance;

/**
 * Initialize API for the Services endpoint.
 *
 * TODO: Merge Axios instances
 */
export function init(config: ApiInstanceConfiguration): void {
  servicesRequestInstance = axios.create({
    baseURL: config.environmentUrls?.services,
    headers: {
      common: setHeaders(DEFAULT_HEADERS_BY_SERVICE.services, config)
    },
    method: HTTP_METHODS.POST,
    timeout: DEFAULT_TIMEOUTS.SERVICES
  });

  /**
   * NOTE: Ensure the latest version of the session ID is used in the `Authorization` header.
   * Previously, the session ID was only set during the application's "bootstrap", but since the
   * application may be "hosted" within an AngularJS application, the session could've expired and be
   * reestablished with a new session ID.
   */
  servicesRequestInstance.interceptors.request.use(updateAuthorizationHeader());

  servicesRequestInstance.interceptors.response.use(
    axiosSuccessResponseInterceptor,
    axiosRejectedResponseInterceptor
  );
}

/** _Retrieve_ data from an API. */
export function retrieveData(
  { headers = {}, path }: ApiOptions,
  data: object = {}
) {
  return servicesRequestInstance.request({
    data,
    headers: merge(
      {},
      servicesRequestInstance.defaults.headers.common,
      headers
    ),
    method: HTTP_METHODS.GET,
    url: path
  });
}

/**
 * _Send_ data to an API.
 *
 * TODO: Merge Axios instances
 */
export function sendDataDeprecated<RequestType = any, ResponseType = any>(
  { headers = {}, path }: ApiParams,
  data: RequestType
) {
  return servicesRequestInstance.post<ResponseType, ResponseType>(path, data, {
    headers: merge({}, servicesRequestInstance.defaults.headers.common, headers)
  });
}

export function sendData<T extends keyof ApiRequestMap>(
  { headers = {}, path }: ApiParams<T>,
  data: {
    payload: T extends keyof ApiRequestMap ? Required<ApiRequestMap>[T] : never;
    faults: T extends keyof ApiFaultMap ? Required<ApiFaultMap>[T] : never;
  }
) {
  return servicesRequestInstance.post<
    T extends keyof ApiResponseMap ? Required<ApiResponseMap>[T] : unknown,
    T extends keyof ApiResponseMap ? Required<ApiResponseMap>[T] : unknown
  >(path, data.payload, {
    headers: merge(
      {},
      servicesRequestInstance.defaults.headers.common,
      headers
    ),
    transformResponse: [
      // @ts-ignore
      ...axios.defaults.transformResponse,
      (responseData) => {
        return {
          ...responseData,
          faultMessages: data.faults
        };
      }
    ]
  });
}

export function getServicesRequestInstance() {
  return servicesRequestInstance;
}
